import { buildId } from '@/components/admin/structure-data/display/helpers'
import { Sortable } from '@/components/admin/structure-data/display/Sortable'
import { EditableStructuredDataSection } from '@/components/admin/structure-data/display/Types'
import { SortableContext } from '@dnd-kit/sortable'
import { Button } from '@repo/ui/button'
import { useT } from '@repo/ui/hooks'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { cn } from '@repo/ui/utils'
import { GripVerticalIcon, Trash2Icon } from 'lucide-react'

export function Section({
  section,
  onDelete,
}: {
  section: EditableStructuredDataSection
  onDelete: (sectionId: string) => void
}) {
  const { tc } = useT()
  return (
    <Sortable id={section.id} effect>
      {({ attributes, listeners, isDragging }) => (
        <div
          className={cn(
            'border-primary-100 flex items-start gap-2 rounded-lg border pb-4',
            isDragging && 'bg-primary-50',
          )}
        >
          <main className="flex-1">
            <header className="border-primary-50 mb-4 flex items-center gap-4 rounded-lg border-b px-4 py-2">
              <TooltipWrapper content={tc('dragAndDropToReorder', { cfl: true })}>
                <Button size="icon" variant="outline" {...attributes} {...listeners}>
                  <GripVerticalIcon className="size-5" />
                </Button>
              </TooltipWrapper>

              <div className="flex-1">
                <h1 className="text-primary-600 text-lg font-medium">{section.label}</h1>
              </div>

              <TooltipWrapper content={tc('delete', { cfl: true })}>
                <Button
                  isSensitive
                  size="icon"
                  variant="outline"
                  onClick={() => onDelete(section.id)}
                >
                  <Trash2Icon className="size-4" />
                </Button>
              </TooltipWrapper>
            </header>

            <SortableContext items={section.items.map((item) => buildId(section.id, item.id))}>
              <div className="flex flex-wrap gap-2 px-4">
                {section.items.length > 0 ? (
                  section.items.map((item, itemIndex) => (
                    <Sortable
                      key={buildId(section.id, item.id)}
                      id={buildId(section.id, item.id)}
                      className="max-w-fit"
                    >
                      {({ attributes, listeners }) => (
                        <div
                          className="border-primary-100 bg-primary-50/50 hover:bg-primary-100 hover:border-primary-200 flex w-fit items-center gap-1 rounded-md border px-2 py-1 transition-colors"
                          {...listeners}
                          {...attributes}
                        >
                          <GripVerticalIcon className="text-primary-400 size-5" />
                          <div className="flex items-center gap-2">
                            <div className="text-base font-medium text-green-500">
                              {itemIndex + 1}
                            </div>
                            <div className="text-sm">{item.label}</div>
                          </div>
                        </div>
                      )}
                    </Sortable>
                  ))
                ) : (
                  <span className="text-orange-400">
                    No items in this section. The section will not be shown unless items are added.
                  </span>
                )}
              </div>
            </SortableContext>
          </main>
        </div>
      )}
    </Sortable>
  )
}
